/**
 * EstateSplitDonuts — "Tell the truth on the dashboard" (Group 3, 1 August
 * 2026 Paul/Beno meeting — tasks/beno-meeting-20260801/IMPLEMENTATION-PLAN.html).
 *
 * Beno's point is LEGAL, not cosmetic: a discretionary trust is a separate
 * legal person, so ITS assets are not part of an individual's net worth.
 * Nothing on the dashboard drew this line explicitly before this card — TWO
 * separate donuts (personal / trust), each broken down by §16 asset type, a
 * combined figure clearly labelled as a COMBINED total (never as personal
 * net worth), and a short factual risk note.
 *
 * Reads the same tenant-wide `GET /v1/estate/assets` (`useEstateAssets`)
 * that already backs `EstateAssetsCard` — confirmed no backend change is
 * needed; every field this card uses is already on the wire. Client-side
 * only:
 *
 *   - The THREE headline currency figures (Personal / Trust / Combined) come
 *     straight off the server's `totals` — NEVER re-summed from the raw
 *     `assets` list. This is the same seam-safety rule `EstateAssetsCard`
 *     follows (its DEFECT B FIX note), and it buys an exact invariant for
 *     free: the backend DEFINES `estateValueRand` as `personOwnedValueRand +
 *     earmarkedValueRand + registeredValueRand` (integer Rand addition —
 *     `estate-assets-list.ts`'s `sumTotals`), so Personal
 *     (personOwned+earmarked) + Trust (registered) === Combined, ALWAYS,
 *     exactly — the one guarantee that matters most on a card whose whole
 *     point is "these numbers add up honestly". Re-deriving any of the three
 *     from cents client-side would reintroduce a possible ±R1 gap between
 *     two SEPARATELY-rounded server buckets and one COMBINED client
 *     rounding — exactly the class of "two disagreeing numbers on one
 *     screen" bug DEFECT B already had to fix once.
 *
 *   - The per-ASSET-TYPE donut slices have no server equivalent (the API
 *     returns per-OWNERSHIP-STATE totals only) — these are grouped from the
 *     raw `assets` list, the same technique `wealth-distribution-model.ts`'s
 *     `buildWealthSlices` uses. Each donut's arcs are proportioned against
 *     the SUM OF ITS OWN SLICES (not the server total) so the ring always
 *     closes at exactly 360° — any sub-Rand gap between that arc denominator
 *     and the header figure is immaterial and invisible at these
 *     magnitudes (same reasoning `WealthDistribution` already relies on).
 *
 * PAUL'S RULING (1 Aug 2026 meeting, confirmed for this build):
 * `earmarked_for_trust` counts as PERSONAL, not trust, in this split. It is
 * INTENT to move an asset into a trust, not registration — legally the asset
 * has not moved yet, title still sits with the individual (or a third
 * party) until the transfer completes. This is NOT a new call: it exactly
 * mirrors `EstateAssetsCard`, which already groups `person_owned` +
 * `earmarked_for_trust` together as "not yet under a trust". Do NOT move
 * earmarked into the trust bucket without a fresh ruling from Paul — that
 * would show a client legal protection they do not have yet, which is
 * precisely the misrepresentation this card exists to fix.
 *
 * KNOWN CROSS-CARD CAVEAT (flagged to Paul, not fixed here — backend change,
 * out of this track's scope): the "Trust assets" total here is
 * `totals.registeredValueRand`, a sum of §16 `AssetDoc.valueZar` rows
 * (`estate-assets-list.ts`). The Hero's own "registered to trusts"
 * split-line figure is a DIFFERENT store — the holdings-projected
 * `TrustDoc.totalValue` (`estate-value.ts`'s file header documents the
 * fork; `EstateAssetsCard`'s DEFECT B FIX comment is the same gotcha hit
 * once already). The two CAN legitimately disagree — one is the asset
 * register, the other is a holdings/AUM valuation — so a small mismatch
 * against Hero's figure is an existing, documented backend fork, not a new
 * bug introduced here.
 */

import { Icon } from '../icon';
import { formatRand, formatRandFromCents } from '../../lib/fmt-r';
import {
  assetTypeLabel,
  useEstateAssets,
  type AssetOwnershipState,
  type AssetType,
  type AssetView,
} from '../../lib/api';
import { TYPE_COLOR } from './wealth-distribution-model';

/** One §16 asset-type slice inside ONE side of the split (personal or trust). */
export interface EstateSplitSlice {
  type: AssetType;
  label: string;
  /** Summed value for this class, in CENTS — used ONLY to proportion this
   *  donut's own arcs (see file header); never shown as a headline figure
   *  (those come from the server `totals`). */
  valueCents: number;
  color: string;
}

export interface EstateSplitBuckets {
  personal: EstateSplitSlice[];
  trust: EstateSplitSlice[];
}

/**
 * PAUL'S RULING (1 Aug 2026 meeting): `earmarked_for_trust` is PERSONAL for
 * this split — intent, not registration. See file header. Exported so the
 * rule is unit-tested directly and can't silently flip in a future edit.
 */
export function isPersonalOwnership(state: AssetOwnershipState): boolean {
  return state === 'person_owned' || state === 'earmarked_for_trust';
}

/** Group→sum→sort one ownership bucket by §16 asset type. Mirrors
 *  `buildWealthSlices` (wealth-distribution-model.ts) exactly — zero/negative
 *  values dropped, largest-first. PURE. */
function groupByType(assets: AssetView[]): EstateSplitSlice[] {
  const byType = new Map<AssetType, number>();
  for (const a of assets) {
    if (!a.valueZar || a.valueZar <= 0) continue;
    byType.set(a.assetType, (byType.get(a.assetType) ?? 0) + a.valueZar);
  }
  const slices: EstateSplitSlice[] = [];
  for (const [type, valueCents] of byType) {
    slices.push({ type, label: assetTypeLabel(type), valueCents, color: TYPE_COLOR[type] });
  }
  return slices.sort((a, b) => b.valueCents - a.valueCents);
}

/**
 * PURE — partitions the tenant's whole asset list (all 3 ownership states)
 * into the two donuts this card draws: PERSONAL (person_owned +
 * earmarked_for_trust, per Paul's ruling above) and TRUST (registered_to_
 * trust only). Unit-tested directly.
 */
export function buildEstateSplitBuckets(assets: AssetView[]): EstateSplitBuckets {
  const personal = assets.filter((a) => isPersonalOwnership(a.ownershipState));
  const trust = assets.filter((a) => a.ownershipState === 'registered_to_trust');
  return { personal: groupByType(personal), trust: groupByType(trust) };
}

/** Inline-SVG donut — one stroked arc per slice, no charting library (same
 *  technique as WealthDistribution's WealthDonut / AllocationCard's
 *  DonutChart). Arcs are proportioned against the SUM OF THE PASSED slices,
 *  never an external total — see file header. */
function SplitDonut({
  slices, testId, ariaLabel,
}: { slices: EstateSplitSlice[]; testId: string; ariaLabel: string }) {
  const size = 118;
  const stroke = 18;
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  const total = slices.reduce((sum, s) => sum + s.valueCents, 0);
  let cum = 0;
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      role="img"
      aria-label={ariaLabel}
      data-testid={testId}
      style={{ flexShrink: 0 }}
    >
      {/* Track */}
      <circle cx={c} cy={c} r={r} fill="none" stroke="var(--surface-2)" strokeWidth={stroke} />
      {slices.map((s) => {
        const dash = total > 0 ? circ * (s.valueCents / total) : 0;
        const arc = (
          <circle
            key={s.type}
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={stroke}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-cum}
            transform={`rotate(-90 ${c} ${c})`}
            strokeLinecap="butt"
          />
        );
        cum += dash;
        return arc;
      })}
    </svg>
  );
}

/** Legend row — colour swatch + a real TEXT label + value + share. Segments
 *  are never distinguished by colour alone (brief requirement) — the label
 *  and the figures carry the meaning; the swatch is a bonus, not the only
 *  signal. */
function LegendRow({ slice, sliceTotalCents }: { slice: EstateSplitSlice; sliceTotalCents: number }) {
  const pct = sliceTotalCents > 0 ? Math.round((slice.valueCents / sliceTotalCents) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5 }}>
      <span
        aria-hidden="true"
        style={{ width: 9, height: 9, borderRadius: 3, background: slice.color, flexShrink: 0 }}
      />
      <span style={{ color: 'var(--ink-2)' }}>{slice.label}</span>
      <span
        style={{
          marginLeft: 'auto', color: 'var(--ink)', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatRandFromCents(slice.valueCents, { cents: false })}
      </span>
      <span style={{ color: 'var(--ink-4)', fontVariantNumeric: 'tabular-nums', minWidth: 30, textAlign: 'right' }}>
        {pct}%
      </span>
    </div>
  );
}

/** Honest empty message for a bucket with nothing in it — never hides the
 *  OTHER (possibly populated) side; see EstateSplitDonuts for the ONE
 *  whole-card hide condition (nothing anywhere). */
function EmptySide({ testId, copy }: { testId: string; copy: string }) {
  return (
    <div
      data-testid={testId}
      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '6px 2px', minHeight: 118 }}
    >
      <svg width={48} height={48} viewBox="0 0 48 48" aria-hidden="true" style={{ flexShrink: 0, opacity: 0.55 }}>
        <circle cx="24" cy="24" r="17" fill="none" stroke="var(--hairline)" strokeWidth="7" strokeDasharray="4 6" />
      </svg>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.6, maxWidth: 220 }}>{copy}</p>
    </div>
  );
}

/** One side of the split — a heading, the server-sourced total, and either
 *  the donut+legend or an honest empty message. `heading` is a bold sub-title
 *  ("Personal assets" / "Trust assets"), NOT the app's `.eyebrow` treatment
 *  (that's reserved for the outer card's small-caps label above) — deliberately
 *  plain inline styling so it reads at a size a reader can actually use as a
 *  chart title. */
function DonutSide({
  heading, caption, slices, totalRand, testId, emptyCopy,
}: {
  heading: string;
  caption: string;
  slices: EstateSplitSlice[];
  /** Server-sourced whole-Rand total for this side — see file header. */
  totalRand: number;
  testId: string;
  emptyCopy: string;
}) {
  const sliceTotalCents = slices.reduce((sum, s) => sum + s.valueCents, 0);
  return (
    <div data-testid={testId} style={{ flex: '1 1 260px', minWidth: 240 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)' }}>{heading}</div>
          <span className="card-meta">{formatRand(totalRand)}</span>
        </div>
        <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5 }}>{caption}</p>
      </div>

      {slices.length === 0 ? (
        <EmptySide testId={`${testId}-empty`} copy={emptyCopy} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <SplitDonut slices={slices} testId={`${testId}-donut`} ariaLabel={`${heading} by asset type`} />
          <div data-testid={`${testId}-legend`} style={{ display: 'flex', flexDirection: 'column', gap: 7, flex: 1, minWidth: 150 }}>
            {slices.map((s) => (
              <LegendRow key={s.type} slice={s} sliceTotalCents={sliceTotalCents} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function EstateSplitDonuts() {
  const { data, isLoading, isError } = useEstateAssets();

  if (isLoading || isError || !data) return null;
  // Matches WealthDistribution's precedent: computed directly on every
  // render (no useMemo) — the tenant's asset list is small and this is a
  // handful of array passes, not a hot loop.
  const buckets = buildEstateSplitBuckets(data.assets);
  // House empty-state idiom (mirrors EstateAssetsCard / ValuesAttentionCard):
  // a quiet card that only appears when it has something to say. Nothing in
  // EITHER bucket means nothing to split — genuinely nothing to add yet.
  if (buckets.personal.length === 0 && buckets.trust.length === 0) return null;

  // PER-FIRM VISIBILITY SCOPES (2026-08-23): `totals` is ABSENT when the client
  // has withheld their portfolio value from the firm acting-as. This whole card
  // exists to display that split, so with no totals to display it renders
  // nothing at all rather than three R0 donuts — the house's own "a quiet card
  // that only appears when it has something to say" idiom, applied honestly.
  // The withheld state is announced once, on the hero, not repeated per card.
  if (!data.totals) return null;

  // Headline figures — server totals, never re-derived. See file header.
  const personalTotalRand = data.totals.personOwnedValueRand + data.totals.earmarkedValueRand;
  const trustTotalRand = data.totals.registeredValueRand;
  const combinedTotalRand = data.totals.estateValueRand;

  return (
    <div className="row">
      <div className="card" data-testid="estate-split-donuts">
        <div className="card-head">
          <div>
            <div className="eyebrow">Personal vs trust</div>
            <h2 className="card-title">Two separate legal estates</h2>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <DonutSide
            heading="Personal assets"
            caption="Owned personally, or intended for a trust but not yet transferred."
            slices={buckets.personal}
            totalRand={personalTotalRand}
            testId="estate-split-personal"
            emptyCopy="Nothing recorded outside a trust yet."
          />
          <DonutSide
            heading="Trust assets"
            caption="Registered to a trust, a separate legal person from its founder, trustees and beneficiaries."
            slices={buckets.trust}
            totalRand={trustTotalRand}
            testId="estate-split-trust"
            emptyCopy="Nothing registered to a trust yet."
          />
        </div>

        <div
          style={{
            marginTop: 26, paddingTop: 22, borderTop: '1px solid var(--hairline)',
            display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start',
          }}
        >
          <div data-testid="estate-split-combined" style={{ flex: '1 1 220px' }}>
            {/* Short eyebrow (matches the app's other 1-3 word eyebrows —
                .eyebrow is uppercase with 0.16em tracking, so a long label
                here would read as shouting); the "not personal net worth"
                disclaimer the brief requires lives in the sentence below,
                where it reads as a clarification, not a caption fragment. */}
            <div className="eyebrow">Combined total</div>
            <div className="figure-serif" style={{ fontWeight: 400, fontSize: 27, color: 'var(--ink)', marginTop: 4 }}>
              {formatRand(combinedTotalRand)}
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.55, maxWidth: 340 }}>
              Personal and trust assets shown together for reference only. A
              trust is a separate legal person, so this combined figure is
              not any one individual&rsquo;s personal net worth.
            </p>
          </div>

          <div
            data-testid="estate-split-risk-note"
            style={{
              flex: '1 1 260px', display: 'flex', gap: 10, background: 'var(--bg-2)',
              border: '1px solid var(--hairline)', borderRadius: 12, padding: '14px 16px',
            }}
          >
            <Icon name="shield" size={16} stroke={2} />
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
              Assets held in trust sit at materially lower risk than assets
              held in a personal name. A trust is a separate legal
              person from the individuals who created or benefit from it.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
